import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { getViewerAccess } from '@generated/server/viewer';
import { useMondayContext } from '@/context/MondayContextProvider';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@components/ui/select';
import { Checkbox } from '@components/ui/checkbox';

const ENQUIRY_TYPE_GUIDE = [
  { value: 'Wedding', description: 'Ceremonies, receptions or both.' },
  { value: 'Social Event', description: 'Personal celebrations — birthdays, bar/bat mitzvahs, engagements, wakes.' },
  { value: 'Corporate Event', description: 'Business-related events — conferences, Christmas parties, product launches.' },
  { value: 'External Catering', description: 'Our catering delivered to a venue or location of your choice.' }
];

const venueOptions = [
  '408 The Venue',
  'CIEL The Venue',
  // 'Cruden Farm',
  'External Catering',
  'Gardens House',
  'Gardens House Marquee',
  'Melbourne Connect',
  'Other',
  'Port Melbourne Yacht Club',
  'Science Gallery',
  'Sophia at the Prahran Arcade',
  'Sushi Baby',
  'The Goods Shed',
  'Undecided'
];

// Major Event venues — must match labels on the board's Venue Preference(s) column
const majorEventVenueOptions = ['VRC MCC', 'F1 GP', 'MotoGP'];

const weddingTypeOptions = [
  'Wedding Ceremony & Reception',
  'Ceremony Only',
  'Reception Only'
];

const socialEventTypeOptions = [
  'After Party', 'Anniversary', 'Baby Shower', 'Baptism', 'Bridal Shower',
  'Birthday', 'Bar/Bat Mitzvah', 'Brunch', 'Charity Event', 'Christening',
  'Christmas Event', 'Cocktail Event', 'Communion', 'Concert', 'Confirmation',
  'Dinner', 'End of Year Party', 'Engagement', 'EOFY', 'Function', 'Gala',
  'Hens/Bucks Do', 'High Tea', 'Photoshoot', 'Private Property', 'Theatre Show',
  'University Event', 'Wake', 'Wine Tasting', 'Other'
];

const corporateEventTypeOptions = [
  'Christmas Event', 'Breakfast', 'Cocktail Event', 'Conference',
  'Exhibition', 'Launch', 'Dinner', 'Luncheon', 'Meeting', 'Other'
];

const industryOptions = [
  'Automotive', 'Agriculture', 'Banking/Finance', 'Building/Construction',
  'Entertainment', 'Fashion/Retail', 'Education', 'Government',
  'Hospitality/Events', 'Media', 'Mining', 'NFP', 'Pharmaceutical/Medical',
  'PR/Marketing', 'Professional Services', 'Real Estate', 'SaaS / IT',
  'Sport', 'Telecommunications', 'Travel Industry', 'Other'
];

const externalCateringEventTypeOptions = [
  'After Party', 'Anniversary', 'Baptism', 'Bar/Bat Mitzvah', 'Birthday',
  'Breakfast', 'Bridal Shower', 'Brunch', 'Charity Event', 'Christening',
  'Christmas Event', 'Cocktail Event', 'Communion', 'Concert', 'Conference',
  'Confirmation', 'Function', 'End of Year Party', 'Engagement', 'EOFY',
  'Exhibition', 'Gala', 'Hens/Bucks Do', 'High Tea', 'Launch', 'Luncheon',
  'Meeting', 'Photoshoot', 'Private Property', 'Seminar', 'Theatre Show',
  'Trade Show', 'University Event', 'Wake', 'Wedding Ceremony',
  'Wedding Ceremony & Reception', 'Wedding Reception', 'Wine Tasting', 'Other'
];

const eventStyleOptions = ['Sit Down', 'Stand Up'];

const budgetOptions = [
  '$100pp to $130pp',
  '$130pp to $160pp',
  '$160pp to $200pp',
  '$200pp +',
  'Unknown',
  'Other'
];

const STAFF_ENQUIRY_TYPES = [
  { value: 'Major Event', description: 'Large-scale events managed by our Major Events team. Staff only.' },
  { value: 'Internal', description: 'Internal Atlantic Group events and bookings. Staff only.' }
];

export default function EventDetailsSection() {
  const form = useFormContext();
  const [isStaff, setIsStaff] = useState(false);
  const { context: mondayContext } = useMondayContext();

  // Inside monday the app receives the signed-in user + account; verify them on the server
  const ctxUser = mondayContext?.user as { id?: string | number } | undefined;
  const ctxAccount = mondayContext?.account as { id?: string | number } | undefined;
  const ctxUserId = ctxUser?.id != null ? String(ctxUser.id) : '';
  const ctxAccountId = ctxAccount?.id != null ? String(ctxAccount.id) : '';

  useEffect(() => {
    if (!ctxUserId || !ctxAccountId) return;
    let active = true;
    getViewerAccess({ data: { userId: ctxUserId, accountId: ctxAccountId } })
      .then((res) => {
        if (!active) return;
        const staff = res?.isStaff ?? false;
        setIsStaff(staff);
        form.setValue('staffUserId', staff ? ctxUserId : '');
        form.setValue('staffAccountId', staff ? ctxAccountId : '');
      })
      .catch((err) => console.error('Could not check staff access:', err));
    return () => { active = false; };
  }, [ctxUserId, ctxAccountId, form]);

  const enquiryTypeOptions = isStaff ? [...ENQUIRY_TYPE_GUIDE, ...STAFF_ENQUIRY_TYPES] : ENQUIRY_TYPE_GUIDE;
  const watchSpecificEnquiryType = form.watch('specificEnquiryType');
  const watchBudget = form.watch('budget');
  const watchVenuePreferences = form.watch('venuePreferences');
  
  return (
    <section className="pb-12 mb-12 border-b border-border">
      <div className="text-center mb-8">
        <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground block mb-3">
          Step 02
        </span>
        <h2 className="text-[28px] md:text-[32px] font-bold text-foreground uppercase tracking-[0.025em] mb-3">
          Event Details
        </h2>
        <p className="text-sm text-muted-foreground">
          Tell us about your event
        </p>
      </div>
      
      <div className="space-y-6">
        <FormField
          control={form.control}
          name="eventName"
          rules={{ required: 'Event name is required' }}
          render={({ field }) => (
            <FormItem data-field={field.name}>
              <FormLabel className="text-sm font-semibold text-foreground">
                Event Name <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input 
                  placeholder="e.g., Wedding of Stella & Matthew"
                  {...field}
                  className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                />
              </FormControl>
              <FormMessage className="text-[13px]" />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="specificEnquiryType"
          rules={{ required: 'Enquiry type is required' }}
          render={({ field }) => (
            <FormItem data-field={field.name}>
              <FormLabel className="text-sm font-semibold text-foreground">
                Enquiry Type <span className="text-destructive">*</span>
              </FormLabel>
              <fieldset aria-label="Enquiry type" className="grid gap-2 sm:grid-cols-2">
                {enquiryTypeOptions.map((t) => {
                  const selected = field.value === t.value;
                  return (
                    <label
                      key={t.value}
                      className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-[4px] border-2 px-4 py-3 text-left transition-all has-[:focus-visible]:shadow-[0_0_0_3px_rgba(33,33,33,0.15)] ${
                        selected
                          ? 'border-foreground bg-muted'
                          : 'border-border bg-background hover:border-foreground/60'
                      }`}
                    >
                      <input
                        type="radio"
                        name={field.name}
                        value={t.value}
                        checked={selected}
                        onChange={() => {
                          // Major Event has its own venue list — clear venues when switching in/out of it
                          if ((field.value === 'Major Event') !== (t.value === 'Major Event')) {
                            form.setValue('venuePreferences', []);
                          }
                          field.onChange(t.value);
                        }}
                        onBlur={field.onBlur}
                        className="sr-only"
                      />
                      <span
                        aria-hidden="true"
                        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                          selected ? 'border-foreground' : 'border-border'
                        }`}
                      >
                        {selected && <span className="h-2 w-2 rounded-full bg-foreground" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-foreground">{t.value}</span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                          {t.description}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </fieldset>
              <FormMessage className="text-[13px]" />
            </FormItem>
          )}
        />
        
        {/* Wedding-specific fields */}
        {watchSpecificEnquiryType === 'Wedding' && (
          <>
            <FormField
              control={form.control}
              name="weddingType"
              rules={{ required: 'Wedding type is required' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground">
                    Wedding Type <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                        <SelectValue placeholder="Select wedding type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {weddingTypeOptions.map((option) => (
                        <SelectItem key={option} value={option} className="text-sm">
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="venuePreferences"
              rules={{ required: 'Venue preference is required' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground">
                    Venue Preference(s) <span className="text-destructive">*</span>
                  </FormLabel>
                  <div className="border border-border rounded-[4px] p-4 space-y-3 max-h-64 overflow-y-auto">
                    {venueOptions.map((venue) => (
                      <div key={venue} className="flex items-center space-x-2">
                        <Checkbox
                          id={`venue-${venue}`}
                          checked={field.value?.includes(venue)}
                          onCheckedChange={(checked) => {
                            const current = field.value || [];
                            if (checked) {
                              field.onChange([...current, venue]);
                            } else {
                              field.onChange(current.filter((v: string) => v !== venue));
                            }
                          }}
                        />
                        <label
                          htmlFor={`venue-${venue}`}
                          className="text-sm cursor-pointer"
                        >
                          {venue}
                        </label>
                      </div>
                    ))}
                  </div>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
            
            {/* Venue (If Other) - conditional field */}
            {watchVenuePreferences?.includes('Other') && (
              <FormField
                control={form.control}
                name="venueIfOther"
                rules={{ required: 'Please specify the other venue' }}
                render={({ field }) => (
                  <FormItem data-field={field.name}>
                    <FormLabel className="text-sm font-semibold text-foreground">
                      Please Specify Venue <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Enter venue name" 
                        {...field}
                        className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                      />
                    </FormControl>
                    <FormMessage className="text-[13px]" />
                  </FormItem>
                )}
              />
            )}
          </>
        )}
        
        {/* Major Event fields (staff only) */}
        {isStaff && watchSpecificEnquiryType === 'Major Event' && (
          <FormField
            control={form.control}
            name="venuePreferences"
            rules={{ required: 'Venue preference is required' }}
            render={({ field }) => (
              <FormItem data-field={field.name}>
                <FormLabel className="text-sm font-semibold text-foreground">
                  Venue Preference(s) <span className="text-destructive">*</span>
                </FormLabel>
                <div className="border border-border rounded-[4px] p-4 space-y-3">
                  {majorEventVenueOptions.map((venue) => (
                    <div key={venue} className="flex items-center space-x-2">
                      <Checkbox
                        id={`major-venue-${venue}`}
                        checked={field.value?.includes(venue)}
                        onCheckedChange={(checked) => {
                          const current: string[] = field.value || [];
                          field.onChange(
                            checked ? [...current, venue] : current.filter((v) => v !== venue)
                          );
                        }}
                      />
                      <label htmlFor={`major-venue-${venue}`} className="text-sm cursor-pointer">
                        {venue}
                      </label>
                    </div>
                  ))}
                </div>
                <FormMessage className="text-[13px]" />
              </FormItem>
            )}
          />
        )}

        {/* Social Event fields */}
        {watchSpecificEnquiryType === 'Social Event' && (
          <>
            <FormField
              control={form.control}
              name="socialEventType"
              rules={{ required: 'Social event type is required' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground">
                    Social Event Type <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                        <SelectValue placeholder="Select social event type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {socialEventTypeOptions.map((option) => (
                        <SelectItem key={option} value={option} className="text-sm">
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="venuePreferences"
              rules={{ required: 'Venue preference is required' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground">
                    Venue Preference(s) <span className="text-destructive">*</span>
                  </FormLabel>
                  <div className="border border-border rounded-[4px] p-4 space-y-3 max-h-64 overflow-y-auto">
                    {venueOptions.map((venue) => (
                      <div key={venue} className="flex items-center space-x-2">
                        <Checkbox
                          id={`venue-${venue}`}
                          checked={field.value?.includes(venue)}
                          onCheckedChange={(checked) => {
                            const current = field.value || [];
                            if (checked) {
                              field.onChange([...current, venue]);
                            } else {
                              field.onChange(current.filter((v: string) => v !== venue));
                            }
                          }}
                        />
                        <label
                          htmlFor={`venue-${venue}`}
                          className="text-sm cursor-pointer"
                        >
                          {venue}
                        </label>
                      </div>
                    ))}
                  </div>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
          </>
        )}
        
        {/* Corporate Event fields */}
        {watchSpecificEnquiryType === 'Corporate Event' && (
          <>
            <FormField
              control={form.control}
              name="corporateEventType"
              rules={{ required: 'Corporate event type is required' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground">
                    Corporate Event Type <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                        <SelectValue placeholder="Select corporate event type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {corporateEventTypeOptions.map((option) => (
                        <SelectItem key={option} value={option} className="text-sm">
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="businessName"
              rules={{ required: 'Business name is required' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground">
                    Business Name/Company <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Acme Corp" 
                      {...field}
                      className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                    />
                  </FormControl>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="industry"
              rules={{ required: 'Industry is required' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground">
                    Industry <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {industryOptions.map((option) => (
                        <SelectItem key={option} value={option} className="text-sm">
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="companyWebsite"
              rules={{ required: 'Company website is required' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground">
                    Company Website <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input 
                      type="text" 
                      placeholder="example.com" 
                      {...field}
                      className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                    />
                  </FormControl>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="venuePreferences"
              rules={{ required: 'Venue preference is required' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground">
                    Venue Preference(s) <span className="text-destructive">*</span>
                  </FormLabel>
                  <div className="border border-border rounded-[4px] p-4 space-y-3 max-h-64 overflow-y-auto">
                    {venueOptions.map((venue) => (
                      <div key={venue} className="flex items-center space-x-2">
                        <Checkbox
                          id={`venue-${venue}`}
                          checked={field.value?.includes(venue)}
                          onCheckedChange={(checked) => {
                            const current = field.value || [];
                            if (checked) {
                              field.onChange([...current, venue]);
                            } else {
                              field.onChange(current.filter((v: string) => v !== venue));
                            }
                          }}
                        />
                        <label
                          htmlFor={`venue-${venue}`}
                          className="text-sm cursor-pointer"
                        >
                          {venue}
                        </label>
                      </div>
                    ))}
                  </div>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
          </>
        )}
        
        {/* External Catering fields */}
        {watchSpecificEnquiryType === 'External Catering' && (
          <>
            <FormField
              control={form.control}
              name="externalCateringEventType"
              rules={{ required: 'External catering event type is required' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground">
                    External Catering Event Type <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {externalCateringEventTypeOptions.map((option) => (
                        <SelectItem key={option} value={option} className="text-sm">
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="externalVenue"
              rules={{ required: 'External venue is required' }}
              render={({ field }) => (
                <FormItem data-field={field.name}>
                  <FormLabel className="text-sm font-semibold text-foreground">
                    External Venue <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="e.g., Hotel Renaissance" 
                      {...field}
                      className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                    />
                  </FormControl>
                  <FormMessage className="text-[13px]" />
                </FormItem>
              )}
            />
          </>
        )}
        
        {/* Other - Please Specify */}
        {watchSpecificEnquiryType === 'Other' && (
          <FormField
            control={form.control}
            name="pleaseSpecify"
            rules={{ required: 'Please specify your event type' }}
            render={({ field }) => (
              <FormItem data-field={field.name}>
                <FormLabel className="text-sm font-semibold text-foreground">
                  Please Specify <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Describe your event type" 
                    {...field}
                    className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                  />
                </FormControl>
                <FormMessage className="text-[13px]" />
              </FormItem>
            )}
          />
        )}
        
        {/* Common fields for all event types */}
        <FormField
          control={form.control}
          name="numberOfGuests"
          rules={{ required: 'Number of guests is required' }}
          render={({ field }) => (
            <FormItem data-field={field.name}>
              <FormLabel className="text-sm font-semibold text-foreground">
                Number of Guests <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  min="1" 
                  placeholder="e.g., 100" 
                  {...field}
                  className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                />
              </FormControl>
              <FormMessage className="text-[13px]" />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="eventStyle"
          render={({ field }) => (
            <FormItem data-field={field.name}>
              <FormLabel className="text-sm font-semibold text-foreground">
                Event Style
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                    <SelectValue placeholder="Select event style" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {eventStyleOptions.map((option) => (
                    <SelectItem key={option} value={option} className="text-sm">
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage className="text-[13px]" />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="budget"
          rules={{ required: 'Budget is required' }}
          render={({ field }) => (
            <FormItem data-field={field.name}>
              <FormLabel className="text-sm font-semibold text-foreground">
                Budget <span className="text-destructive">*</span>
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]">
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {budgetOptions.map((option) => (
                    <SelectItem key={option} value={option} className="text-sm">
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage className="text-[13px]" />
            </FormItem>
          )}
        />
        
        {watchBudget === 'Other' && (
          <FormField
            control={form.control}
            name="budgetOther"
            rules={{ required: 'Please specify your budget per person' }}
            render={({ field }) => (
              <FormItem data-field={field.name}>
                <FormLabel className="text-sm font-semibold text-foreground">
                  Budget per person (AUD) <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    placeholder="e.g., 150.00" 
                    {...field}
                    className="h-11 text-sm border-border focus:border-ring focus:shadow-[0_0_0_3px_rgba(33,33,33,0.08)]"
                  />
                </FormControl>
                <FormMessage className="text-[13px]" />
              </FormItem>
            )}
          />
        )}
      </div>
    </section>
  );
}
